import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Upload } from "lucide-react";
import PhotoGrid from "@/components/PhotoGrid";
import { pub, client, getToken, errMsg } from "@/lib/api";

function Uploader({ kind, onDone }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a photo first.");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setBusy(true);
    try {
      await client(kind).post("/photos", fd);
      toast.success("Photo shared. Faʻafetai!");
      setFile(null); setCaption(""); ref.current.value = "";
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={upload} className="card-warm grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end" data-testid="photo-upload-form">
      <label className="block"><span className="block text-lg font-semibold mb-2">Choose a photo</span>
        <input ref={ref} type="file" accept="image/*" data-testid="photo-upload-input" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-lg file:mr-4 file:min-h-[48px] file:px-5 file:rounded-full file:border-0 file:bg-[#2B5338] file:text-white file:font-semibold" /></label>
      <label className="block"><span className="block text-lg font-semibold mb-2">Caption (optional)</span>
        <input data-testid="photo-caption-input" className="field" maxLength={300} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Who's in the photo?" /></label>
      <button disabled={busy} data-testid="photo-upload-submit" className="btn-primary"><Upload className="w-5 h-5" />{busy ? "Sharing…" : "Share photo"}</button>
    </form>
  );
}

export default function Photos() {
  const qc = useQueryClient();
  const { data: photos = [], isLoading } = useQuery({ queryKey: ["photos"], queryFn: () => pub.get("/photos").then((r) => r.data) });
  const kind = getToken("admin") ? "admin" : getToken("family") ? "family" : null;
  const [myId, setMyId] = useState(null);
  useEffect(() => {
    if (kind === "family") client("family").get("/family/me").then((r) => setMyId(r.data.id)).catch(() => {});
  }, [kind]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["photos"] });
  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try { await client(kind).delete(`/photos/${p.id}`); toast.success("Photo removed"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 space-y-8">
      <div><p className="label-cap">Memories</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-amber-950 mt-2 flex items-center gap-3"><Camera className="w-10 h-10 text-[#8C3415]" />Family photos</h1></div>
      {kind ? <Uploader kind={kind} onDone={refresh} /> : (
        <div data-testid="photo-signin-prompt" className="card-warm !bg-[#FDF6ED] text-lg">
          To share photos, <Link to="/register" className="underline font-bold">register your family</Link> or <Link to="/my-family" className="underline font-bold">sign in to My Family</Link>. Everyone can look at the gallery.
        </div>
      )}
      {isLoading ? <p className="text-lg">Loading…</p> : (
        <PhotoGrid photos={photos} onDelete={remove} canDelete={(p) => kind === "admin" || (myId && p.family_id === myId)} />
      )}
    </div>
  );
}
